'use client';

import { createHttpClient } from '@/lib/api/httpClient';
import {
  mapTpSlOrder,
  toLimitOrderDto,
  toMarketOrderDto,
  toTpSlOrderDto,
} from '@/lib/api/order/mappers';
import type {
  LimitOrderRequest,
  MarketOrderRequest,
  OrderResponseDto,
  TpSlOrder,
  TpSlOrderDto,
  TpSlOrderQueryParams,
  TpSlOrderRequest,
} from '@/lib/api/order/types';

const BASE_URL = process.env.NEXT_PUBLIC_API_ORDER_URL;

// Cookie-based order client — auth rides the HttpOnly cookie
const orderHttp = createHttpClient({ baseUrl: BASE_URL });

export async function placeMarketOrder(body: MarketOrderRequest) {
  return orderHttp.post<OrderResponseDto>('/orders/market', { body: toMarketOrderDto(body) });
}

export async function placeLimitOrder(body: LimitOrderRequest) {
  return orderHttp.post<OrderResponseDto>('/orders/limit', { body: toLimitOrderDto(body) });
}

/**
 * Cancel a limit order by ID
 * Endpoint: DELETE /orders/limit/{id}
 */
export async function cancelLimitOrder(orderId: string): Promise<OrderResponseDto> {
  return orderHttp.delete<OrderResponseDto>(`/orders/limit/${orderId}`);
}

/**
 * Update a limit order by ID
 * Endpoint: PUT /orders/limit/{id}
 */
export async function updateLimitOrder(
  orderId: string,
  body: LimitOrderRequest,
): Promise<OrderResponseDto> {
  return orderHttp.put<OrderResponseDto>(`/orders/limit/${orderId}`, {
    body: toLimitOrderDto(body),
  });
}

/**
 * Get TP/SL orders
 * Endpoint: GET /orders/tpsl
 */
export async function getTpSlOrders(params: TpSlOrderQueryParams): Promise<TpSlOrder[]> {
  const dto = await orderHttp.get<TpSlOrderDto[]>('/orders/tpsl', {
    query: {
      assetId: params.assetId,
      ...(params.status ? { status: params.status } : {}),
      ...(params.primary !== undefined ? { primary: params.primary } : {}),
    },
  });
  return dto.map(mapTpSlOrder);
}

/**
 * Create TP/SL order
 * Endpoint: POST /orders/tpsl
 */
export async function createTpSlOrder(body: TpSlOrderRequest): Promise<OrderResponseDto> {
  return orderHttp.post<OrderResponseDto>('/orders/tpsl', { body: toTpSlOrderDto(body) });
}

/**
 * Update TP/SL order by ID
 * Endpoint: PUT /orders/tpsl/{id}
 */
export async function updateTpSlOrder(
  orderId: string,
  body: TpSlOrderRequest,
): Promise<OrderResponseDto> {
  return orderHttp.put<OrderResponseDto>(`/orders/tpsl/${orderId}`, { body: toTpSlOrderDto(body) });
}

/**
 * Delete TP/SL order by ID
 * Endpoint: DELETE /orders/tpsl/{id}
 */
export async function deleteTpSlOrder(orderId: string): Promise<OrderResponseDto> {
  return orderHttp.delete<OrderResponseDto>(`/orders/tpsl/${orderId}`);
}
